import React, { useEffect, useRef, useState } from "react";
import UnlimitInput from "./ui-kit/unlimit-input";
import { useForm } from "react-hook-form";
import faqTypeHttpService from "../services/http/settings/faq-type.service";
import UnlimitSubmitButton from "./ui-kit/unlimit-submit-button";
import httpService from "../services/http/settings/faq.service";
import notificationService from "../services/toaster.service";
import { store } from "../core/redux/store";
import { hydrateErrors } from "../utils/error.utils";
import UnlimitSelect from "./ui-kit/unlimit-select";
import UnlimitToggle from "./ui-kit/unlimit-toggle";
import { useTranslation } from "react-i18next";
import UnlimitFileUpload from "./ui-kit/unlimit-fileupload";
import useRoleAndPerm from "../utils/permissionFunction";
import { useSelector } from "react-redux";
import UnlimitTextarea from "./ui-kit/unlimit-textarea";

const FaqDetail = ({ onHide, faq }) => {
  const {
    register,
    handleSubmit,
    formState,
    setValue,
    setError,
    reset,
    control,
    getValues,
    watch
  } = useForm({
    defaultValues: {
      status: faq ? faq.is_active === 1 : true
    }
  });
  
  const { errors } = formState;
  const [isLoading, setIsLoading] = useState(false);
  const [designationLoading, setDesignationLoading] = useState(false);
  const [designations, setDesignations] = useState([]);
  
  const companies = useSelector((state) => state.companies.companies);
  const modalRef = useRef();
  const { auth, locations } = store.getState();
  const { t } = useTranslation();
  
  // Watch status field for debugging
  const statusValue = watch("status");
  
  const onSubmitForm = async (data) => {
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("question", data.question);
      formData.append("answer", data.answer);
      formData.append("status", data.status ? 1 : 0);
      formData.append("faq_type_id", data.faq_type_id.id);
      
      if (faq) {
        formData.append("id", faq.id);
        await updateFaq(formData);
      } else {
        await createFaq(formData);
      }
    } catch (error) {
      console.error("Form submission error:", error);
      if (error.validation_error && error.validation_error.errors) {
        hydrateErrors(error.validation_error.errors, setError);
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const createFaq = async (formData) => {
    try {
      const response = await httpService.createFaq(formData);
      if (response && response.status === 200) {
        notificationService.successMessage(
          t("faq.faqCreateSuccess")
        );
        onHide();
        reset();
        modalHide();
      }
    } catch (error) {
      if (error.validation_error && error.validation_error.errors) {
        hydrateErrors(error.validation_error.errors, setError);
      }
    }
  };
  
  const updateFaq = async (formData) => {
    try {
      const response = await httpService.updateFaq(formData);
      if (response && response.status === 200) {
        notificationService.successMessage(
          t("faq.faqUpdateSuccess")
        );
        onHide();
        reset();
        modalHide();
      }
    } catch (error) {
      if (error.validation_error && error.validation_error.errors) {
        hydrateErrors(error.validation_error.errors, setError);
      }
    }
  };
  
  async function fetchDesignation() {
    setDesignationLoading(true);
    try {
      const designation = await faqTypeHttpService.faqTypeList({
        status: 1,
      });
      if (designation?.data?.data?.data) {
        setDesignations(designation.data.data.data);
      }
    } catch (err) {
      console.error("Error fetching designation data:", err);
    } finally {
      setDesignationLoading(false);
    }
  }
  
  const modalHide = () => {
    const cancelButton = document.querySelector(".btn-cancel");
    if (cancelButton) {
      cancelButton.click();
    }
  };
  
  useEffect(() => {
    fetchDesignation();
  }, []);
  
  useEffect(() => {
    if (faq) {
      setValue("question", faq.question);
      setValue("answer", faq.answer);
      setValue("status", faq.is_active === 1);
      
      if (faq.faq_type) {
        setValue("faq_type_id", {
          ...faq.faq_type,
          label: faq.faq_type.name,
          value: faq.faq_type.id,
        });
      }
    }
  }, [faq, setValue]);
  
  useEffect(() => {
    const modalElement = modalRef.current;
    
    const handleShow = () => {
      // Handle modal show logic if needed
    };
    
    const handleHide = () => {
      reset();
      onHide();
    };
    
    if (modalElement) {
      modalElement.addEventListener("show.bs.modal", handleShow);
      modalElement.addEventListener("hide.bs.modal", handleHide);
      
      return () => {
        modalElement.removeEventListener("show.bs.modal", handleShow);
        modalElement.removeEventListener("hide.bs.modal", handleHide);
      };
    }
  }, [onHide, reset]);
  
  
  return (
    <div>
      <div className="modal fade" id="add-faq" ref={modalRef}>
        <div className="modal-dialog modal-dialog-centered custom-modal-two">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header border-0 custom-modal-header">
                  <div></div>
                  <button
                    type="button"
                    className="close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
                <div className="modal-body custom-modal-body">
                  <form onSubmit={handleSubmit(onSubmitForm)}>
                    <div className="row">
                      <div className="col-lg-12 mb-3">
                        <UnlimitSelect
                          label={t("inputField.label.faqType")}
                          id="faq_type_id"
                          errors={errors}
                          control={control}
                          isLoading={designationLoading}
                          register={register}
                          rules={{
                            required: t("inputField.validation.required"),
                          }}
                          placeholder={t(
                            "inputField.label.faqType"
                          )}
                          options={designations}
                          optionLabel="name"
                          optionValue="id"
                        />
                      </div>
                      <div className="col-lg-12 mb-3">
                        <UnlimitTextarea
                          label={t("tableColumnName.question")}
                          id="question"
                          type="text"
                          placeholder={t("inputField.placeholder.enterQuestion")}
                          register={register}
                          rules={{
                            required: t("inputField.validation.required")
                          }}
                          error={errors.question}
                          errorMessage={errors.question?.message}
                        />
                      </div>
                      <div className="col-lg-12 mb-3">
                        <UnlimitTextarea
                          label={t("tableColumnName.answer")}
                          id="answer"
                          type="text"
                          placeholder={t("inputField.placeholder.enterAnswer")}
                          register={register}
                          rules={{
                            required: t("inputField.validation.required")
                          }}
                          error={errors.answer}
                          errorMessage={errors.answer?.message}
                        />
                      </div>
                    </div>
                    <div className="row mb-3">
                      <div className="input-blocks m-0">
                        <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                          <UnlimitToggle
                            isChecked={watch("status")}
                            label={t("tableColumnName.status")}
                            id="status"
                            register={register}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="modal-footer-btn">
                      <button
                        type="button"
                        className="btn btn-cancel me-2"
                        data-bs-dismiss="modal"
                      >
                        {t("general.button.cancel")}
                      </button>
                      <UnlimitSubmitButton
                        label={
                          faq
                            ? t("general.button.update")
                            : t("general.button.submit")
                        }
                        isLoading={isLoading}
                      />
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FaqDetail;